import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getCurrentUserProfile, updateCurrentUserProfile, formatApiError, getProfilePictureUrl, changePassword } from '../services/api';
import { getDateLocale } from '../i18n';
import AdminTopBar from './AdminTopBar';
import ProfilePictureUpload from './ProfilePictureUpload';
import MFASettings from './MFASettings';

const AdminSettings = () => {
  const { t } = useTranslation(['profile', 'common']);
  const navigate = useNavigate();
  const [userData, setUserData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    role: '',
    date_joined: '',
    team: null,
    manager: null,
    profile_picture: null,
    initials: ''
  });
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: ''
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});

  // {t('changePassword')} modal
  const [changePasswordModalOpen, setChangePasswordModalOpen] = useState(false);
  const [changePasswordForm, setChangePasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [changePasswordSaving, setChangePasswordSaving] = useState(false);
  const [changePasswordFieldErrors, setChangePasswordFieldErrors] = useState({});
  const [changePasswordGeneralError, setChangePasswordGeneralError] = useState(null);
  const [changePasswordSuccess, setChangePasswordSuccess] = useState(false);
  const [showChangePasswordFields, setShowChangePasswordFields] = useState({ current: false, new: false, confirm: false });

  // Fetch user data on mount
  useEffect(() => {
    const fetchUserData = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await getCurrentUserProfile();
        setUserData(data);
        setFormData({
          first_name: data.first_name || '',
          last_name: data.last_name || ''
        });
      } catch (err) {
        console.error('Failed to fetch user profile:', err);
        setError(t('loadFailed'));
      } finally {
        setLoading(false);
      }
    };

    fetchUserData();
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear field error when user starts typing
    if (fieldErrors[name]) {
      setFieldErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
    // Clear success message when user makes changes
    if (success) {
      setSuccess(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    setSuccess(false);
    setFieldErrors({});

    try {
      const updatedData = await updateCurrentUserProfile({
        first_name: formData.first_name,
        last_name: formData.last_name
      });
      
      // Update user data with response
      setUserData(updatedData);
      setSuccess(true);
      
      // Clear success message after 3 seconds
      setTimeout(() => {
        setSuccess(false);
      }, 3000);
    } catch (err) {
      console.error('Failed to update profile:', err);
      const formattedError = formatApiError(err);
      setError(formattedError.generalError || t('updateFailed'));
      setFieldErrors(formattedError.fieldErrors || {});
    } finally {
      setSaving(false);
    }
  };

  const handleBack = () => {
    navigate('/admin-dashboard');
  };

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && changePasswordModalOpen && !changePasswordSaving) {
        setChangePasswordModalOpen(false);
        setChangePasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
        setChangePasswordFieldErrors({});
        setChangePasswordGeneralError(null);
        setChangePasswordSuccess(false);
      }
    };
    if (changePasswordModalOpen) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [changePasswordModalOpen, changePasswordSaving]);

  const openChangePasswordModal = () => {
    setChangePasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
    setChangePasswordFieldErrors({});
    setChangePasswordGeneralError(null);
    setChangePasswordSuccess(false);
    setShowChangePasswordFields({ current: false, new: false, confirm: false });
    setChangePasswordModalOpen(true);
  };

  const closeChangePasswordModal = () => {
    setChangePasswordModalOpen(false);
    setChangePasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
    setChangePasswordFieldErrors({});
    setChangePasswordGeneralError(null);
    setChangePasswordSuccess(false);
  };

  const handleChangePasswordInputChange = (field, value) => {
    setChangePasswordForm(prev => ({ ...prev, [field]: value }));
    setChangePasswordFieldErrors(prev => {
      const next = { ...prev };
      delete next[field === 'currentPassword' ? 'old_password' : field === 'newPassword' ? 'new_password' : 'confirm_password'];
      return next;
    });
    setChangePasswordGeneralError(null);
  };

  const validateChangePasswordForm = () => {
    const err = {};
    if (!changePasswordForm.currentPassword.trim()) err.old_password = [t('currentPasswordRequired')];
    if (!changePasswordForm.newPassword) err.new_password = [t('newPasswordRequired')];
    else if (changePasswordForm.newPassword.length < 8) err.new_password = [t('passwordMinLength')];
    if (changePasswordForm.newPassword !== changePasswordForm.confirmPassword) err.confirm_password = [t('passwordsDoNotMatch')];
    if (!changePasswordForm.confirmPassword) err.confirm_password = [t('confirmPasswordRequired')];
    setChangePasswordFieldErrors(err);
    return Object.keys(err).length === 0;
  };

  const handleChangePasswordSubmit = async (e) => {
    e.preventDefault();
    setChangePasswordGeneralError(null);
    if (!validateChangePasswordForm()) return;
    setChangePasswordSaving(true);
    try {
      await changePassword(
        changePasswordForm.currentPassword,
        changePasswordForm.newPassword,
        changePasswordForm.confirmPassword
      );
      setChangePasswordSuccess(true);
      setTimeout(closeChangePasswordModal, 1500);
    } catch (err) {
      const formatted = formatApiError(err);
      setChangePasswordGeneralError(formatted.generalError || t('changePasswordFailed'));
      setChangePasswordFieldErrors(formatted.fieldErrors || {});
    } finally {
      setChangePasswordSaving(false);
    }
  };

  const getChangePasswordFieldError = (apiKey) => {
    const msg = changePasswordFieldErrors[apiKey];
    return Array.isArray(msg) ? msg[0] : msg || null;
  };

  const roleLabels = {
    'EMPLOYE': t('roleEmployee'),
    'MANAGER': t('roleManager'),
    'ADMIN': t('roleAdmin')
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString(getDateLocale(), {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <div className="from-slate-50 to-slate-100 text-text-main overflow-hidden h-screen flex flex-col">
      <main className="flex-1 flex flex-col h-full overflow-hidden relative w-full">
        <AdminTopBar
          title={t('pageTitle')}
          searchPlaceholder=""
          onSearch={() => {}}
          onMenuClick={() => {}}
          onHelpClick={() => {}}
          onSettingsClick={() => {}}
          showSearch={false}
        />
        <div className="flex-1 overflow-y-auto p-6 scroll-smooth">
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ duration: 0.3, ease: [0.4, 0, 0.2, 1] }}
            className="max-w-[1400px] mx-auto flex flex-col gap-8"
          >
            {/* Header - same as Employees profile page */}
            <div className="flex items-center justify-between mb-8">
              <div>
                <h1 className="text-3xl font-bold text-slate-900">{t('settingsTitle')}</h1>
                <p className="text-slate-600 mt-1">{t('settingsSubtitle')}</p>
              </div>
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={openChangePasswordModal}
                  className="inline-flex items-center justify-center gap-2 rounded-md h-9 px-3 bg-white text-slate-700 text-sm font-medium border border-slate-300 hover:bg-slate-50 transition-colors"
                >
                  <span className="material-symbols-outlined text-[18px]">lock</span>
                  {t('changePassword')}
                </button>
                <button
                  type="button"
                  onClick={handleBack}
                  className="inline-flex items-center justify-center gap-2 rounded-md h-9 px-3 bg-white text-slate-700 text-sm font-medium border border-slate-300 hover:bg-slate-50 transition-colors"
                >
                  <span className="material-symbols-outlined text-[18px]">arrow_back</span>
                  {t('backToDashboard')}
                </button>
              </div>
            </div>

            {/* Success Message */}
            {success && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-emerald-50 border border-emerald-200 text-emerald-700 dark:bg-emerald-900/30 dark:border-emerald-700/50 dark:text-emerald-200 px-4 py-3 rounded-lg flex items-center gap-2"
              >
                <span className="material-symbols-outlined text-[20px]">check_circle</span>
                <span className="text-sm font-medium">{t('profileUpdated')}</span>
              </motion.div>
            )}

            {/* Error Message */}
            {error && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-red-50 border border-red-200 text-red-700 dark:bg-red-900/30 dark:border-red-700/50 dark:text-red-200 px-4 py-3 rounded-lg flex items-center gap-2"
              >
                <span className="material-symbols-outlined text-[20px]">error</span>
                <span className="text-sm font-medium">{error}</span>
              </motion.div>
            )}

            {/* Loading State */}
            {loading ? (
              <div className="bg-white rounded-xl border border-slate-200 p-12 flex items-center justify-center">
                <div className="text-slate-500">{t('loadingProfile')}</div>
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-6">
                {/* Profile and {t('personalInformation')} */}
                <div className="space-y-6">
                  {/* Single Card: Profile Picture + {t('personalInformation')} */}
                  <div className="p-6 bg-white rounded-xl border border-slate-200 shadow-sm">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                      <div className="flex flex-col items-center justify-center">
                        <ProfilePictureUpload
                          layout="inline"
                          currentPictureUrl={getProfilePictureUrl(userData.profile_picture)}
                          initials={userData.initials || ''}
                          onUpdate={(updatedData) => setUserData(updatedData)}
                        />
                      </div>
                      <div className="md:col-span-2">
                        <h3 className="font-semibold text-slate-900 mb-4">{t('personalInformation')}</h3>
                        <form onSubmit={handleSubmit} id="admin-profile-form">
                          <div className="space-y-3">
                            <div className="grid grid-cols-2 gap-3">
                              <div>
                                <label className="text-sm font-medium text-slate-700">{t('firstName')}</label>
                                <input
                                  type="text"
                                  name="first_name"
                                  value={formData.first_name}
                                  onChange={handleInputChange}
                                  className={`mt-1 w-full h-9 rounded-md border px-3 text-sm ${
                                    fieldErrors.first_name ? 'border-red-300 bg-red-50 dark:border-red-700 dark:bg-red-900/30' : 'border-slate-200 bg-white dark:border-slate-600 dark:bg-slate-700 dark:text-slate-100'
                                  } focus:outline-none focus:ring-1 focus:ring-slate-300`}
                                  placeholder={t('firstNamePlaceholder')}
                                  disabled={saving}
                                />
                                {fieldErrors.first_name && (
                                  <p className="text-red-600 text-xs mt-1">{fieldErrors.first_name[0]}</p>
                                )}
                              </div>
                              <div>
                                <label className="text-sm font-medium text-slate-700">{t('lastName')}</label>
                                <input
                                  type="text"
                                  name="last_name"
                                  value={formData.last_name}
                                  onChange={handleInputChange}
                                  className={`mt-1 w-full h-9 rounded-md border px-3 text-sm ${
                                    fieldErrors.last_name ? 'border-red-300 bg-red-50 dark:border-red-700 dark:bg-red-900/30' : 'border-slate-200 bg-white dark:border-slate-600 dark:bg-slate-700 dark:text-slate-100'
                                  } focus:outline-none focus:ring-1 focus:ring-slate-300`}
                                  placeholder={t('lastNamePlaceholder')}
                                  disabled={saving}
                                />
                                {fieldErrors.last_name && (
                                  <p className="text-red-600 text-xs mt-1">{fieldErrors.last_name[0]}</p>
                                )}
                              </div>
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                              <div>
                                <label className="text-sm font-medium text-slate-700">{t('email')}</label>
                                <input
                                  type="email"
                                  value={userData.email || ''}
                                  disabled
                                  className="mt-1 w-full h-9 rounded-md border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-700 px-3 text-sm text-slate-600 dark:text-slate-400"
                                />
                                <p className="text-xs text-slate-500 mt-1">{t('emailCannotChange')}</p>
                              </div>
                              <div>
                                <label className="text-sm font-medium text-slate-700">{t('role')}</label>
                                <input
                                  type="text"
                                  value={roleLabels[userData.role] || userData.role || ''}
                                  disabled
                                  className="mt-1 w-full h-9 rounded-md border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-700 px-3 text-sm text-slate-600 dark:text-slate-400"
                                />
                                <p className="text-xs text-slate-500 mt-1">{t('roleCannotChange')}</p>
                              </div>
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                              {userData.team ? (
                                <div>
                                  <label className="text-sm font-medium text-slate-700">{t('team')}</label>
                                  <input
                                    type="text"
                                    value={userData.team.name || ''}
                                    disabled
                                    className="mt-1 w-full h-9 rounded-md border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-700 px-3 text-sm text-slate-600 dark:text-slate-400"
                                  />
                                  <p className="text-xs text-slate-500 mt-1">{t('teamCannotChange')}</p>
                                </div>
                              ) : (
                                <div />
                              )}
                              <div>
                                <label className="text-sm font-medium text-slate-700">{t('dateJoined')}</label>
                                <input
                                  type="text"
                                  value={formatDate(userData.date_joined)}
                                  disabled
                                  className="mt-1 w-full h-9 rounded-md border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-700 px-3 text-sm text-slate-600 dark:text-slate-400"
                                />
                              </div>
                            </div>
                            {userData.manager && (
                              <div className="grid grid-cols-2 gap-3">
                                <div>
                                  <label className="text-sm font-medium text-slate-700">{t('manager')}</label>
                                  <input
                                    type="text"
                                    value={
                                      userData.manager.first_name || userData.manager.last_name
                                        ? `${userData.manager.first_name || ''} ${userData.manager.last_name || ''}`.trim()
                                        : userData.manager.email || ''
                                    }
                                    disabled
                                    className="mt-1 w-full h-9 rounded-md border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-700 px-3 text-sm text-slate-600 dark:text-slate-400"
                                  />
                                  <p className="text-xs text-slate-500 mt-1">{t('managerCannotChange')}</p>
                                </div>
                              </div>
                            )}
                          </div>
                        </form>
                      </div>
                    </div>
                    <div className="flex justify-end gap-3 mt-6 pt-6 border-t border-slate-200">
                      <button
                        type="button"
                        onClick={handleBack}
                        className="rounded-md h-9 px-3 text-sm font-medium border border-slate-300 bg-white hover:bg-slate-50 text-slate-700"
                      >
                        {t('common:cancel')}
                      </button>
                      <button
                        type="submit"
                        form="admin-profile-form"
                        disabled={saving}
                        className="rounded-md h-9 px-3 text-sm font-medium bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {success ? t('changesSaved') : t('saveChanges')}
                      </button>
                    </div>
                  </div>

                  <MFASettings />
                </div>
              </div>
            )}
          </motion.div>
        </div>

        {/* {t('changePassword')} Modal */}
            <AnimatePresence>
              {changePasswordModalOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={closeChangePasswordModal}>
                  <motion.div
                    initial={{ opacity: 0, scale: 0.96 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.96 }}
                    transition={{ duration: 0.2 }}
                    className="bg-white rounded-xl border border-slate-200 shadow-xl max-w-md w-full overflow-hidden"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
                      <h3 className="text-lg font-bold text-slate-900">{t('changePassword')}</h3>
                      <button
                        type="button"
                        onClick={closeChangePasswordModal}
                        className="p-1 rounded-lg text-slate-500 hover:bg-slate-100 transition-colors"
                        aria-label={t('common:close')}
                      >
                        <span className="material-symbols-outlined text-[24px]">close</span>
                      </button>
                    </div>
                    <form onSubmit={handleChangePasswordSubmit} className="p-6 space-y-4">
                      {changePasswordSuccess && (
                        <div className="bg-emerald-50 border border-emerald-200 text-emerald-700 dark:bg-emerald-900/30 dark:border-emerald-700/50 dark:text-emerald-200 px-4 py-3 rounded-lg flex items-center gap-2 text-sm">
                          <span className="material-symbols-outlined text-[20px]">check_circle</span>
                          <span>{t('passwordChanged')}</span>
                        </div>
                      )}
                      {changePasswordGeneralError && (
                        <div className="bg-red-50 border border-red-200 text-red-700 dark:bg-red-900/30 dark:border-red-700/50 dark:text-red-200 px-4 py-3 rounded-lg flex items-center gap-2 text-sm">
                          <span className="material-symbols-outlined text-[20px] shrink-0">error</span>
                          <span>{changePasswordGeneralError}</span>
                        </div>
                      )}
                      <div className="space-y-2">
                        <label className="text-slate-700 text-sm font-semibold block">{t('currentPassword')}</label>
                        <div className="relative">
                          <input
                            type={showChangePasswordFields.current ? 'text' : 'password'}
                            value={changePasswordForm.currentPassword}
                            onChange={(e) => handleChangePasswordInputChange('currentPassword', e.target.value)}
                            className={`w-full h-10 rounded-lg border px-4 pr-10 text-slate-900 text-sm focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary ${
                              getChangePasswordFieldError('old_password') ? 'border-red-300 bg-red-50 dark:border-red-700 dark:bg-red-900/30' : 'border-slate-200 bg-white dark:border-slate-600 dark:bg-slate-700 dark:text-slate-100'
                            }`}
                            placeholder={t('currentPasswordPlaceholder')}
                            disabled={changePasswordSaving}
                            autoComplete="current-password"
                          />
                          <button
                            type="button"
                            onClick={() => setShowChangePasswordFields(prev => ({ ...prev, current: !prev.current }))}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300"
                            tabIndex={-1}
                            aria-label={showChangePasswordFields.current ? 'Hide password' : 'Show password'}
                          >
                            <span className="material-symbols-outlined text-[20px]">{showChangePasswordFields.current ? 'visibility_off' : 'visibility'}</span>
                          </button>
                        </div>
                        {getChangePasswordFieldError('old_password') && (
                          <p className="text-red-600 text-xs">{getChangePasswordFieldError('old_password')}</p>
                        )}
                      </div>
                      <div className="space-y-2">
                        <label className="text-slate-700 text-sm font-semibold block">{t('newPassword')}</label>
                        <div className="relative">
                          <input
                            type={showChangePasswordFields.new ? 'text' : 'password'}
                            value={changePasswordForm.newPassword}
                            onChange={(e) => handleChangePasswordInputChange('newPassword', e.target.value)}
                            className={`w-full h-10 rounded-lg border px-4 pr-10 text-slate-900 text-sm focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary ${
                              getChangePasswordFieldError('new_password') ? 'border-red-300 bg-red-50 dark:border-red-700 dark:bg-red-900/30' : 'border-slate-200 bg-white dark:border-slate-600 dark:bg-slate-700 dark:text-slate-100'
                            }`}
                            placeholder={t('newPasswordPlaceholder')}
                            disabled={changePasswordSaving}
                            autoComplete="new-password"
                          />
                          <button
                            type="button"
                            onClick={() => setShowChangePasswordFields(prev => ({ ...prev, new: !prev.new }))}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300"
                            tabIndex={-1}
                            aria-label={showChangePasswordFields.new ? 'Hide password' : 'Show password'}
                          >
                            <span className="material-symbols-outlined text-[20px]">{showChangePasswordFields.new ? 'visibility_off' : 'visibility'}</span>
                          </button>
                        </div>
                        {getChangePasswordFieldError('new_password') && (
                          <p className="text-red-600 text-xs">{getChangePasswordFieldError('new_password')}</p>
                        )}
                      </div>
                      <div className="space-y-2">
                        <label className="text-slate-700 text-sm font-semibold block">{t('confirmNewPassword')}</label>
                        <div className="relative">
                          <input
                            type={showChangePasswordFields.confirm ? 'text' : 'password'}
                            value={changePasswordForm.confirmPassword}
                            onChange={(e) => handleChangePasswordInputChange('confirmPassword', e.target.value)}
                            className={`w-full h-10 rounded-lg border px-4 pr-10 text-slate-900 text-sm focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary ${
                              getChangePasswordFieldError('confirm_password') ? 'border-red-300 bg-red-50 dark:border-red-700 dark:bg-red-900/30' : 'border-slate-200 bg-white dark:border-slate-600 dark:bg-slate-700 dark:text-slate-100'
                            }`}
                            placeholder={t('confirmPasswordPlaceholder')}
                            disabled={changePasswordSaving}
                            autoComplete="new-password"
                          />
                          <button
                            type="button"
                            onClick={() => setShowChangePasswordFields(prev => ({ ...prev, confirm: !prev.confirm }))}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300"
                            tabIndex={-1}
                            aria-label={showChangePasswordFields.confirm ? 'Hide password' : 'Show password'}
                          >
                            <span className="material-symbols-outlined text-[20px]">{showChangePasswordFields.confirm ? 'visibility_off' : 'visibility'}</span>
                          </button>
                        </div>
                        {getChangePasswordFieldError('confirm_password') && (
                          <p className="text-red-600 text-xs">{getChangePasswordFieldError('confirm_password')}</p>
                        )}
                      </div>
                      <div className="flex justify-end gap-3 pt-2">
                        <button
                          type="button"
                          onClick={closeChangePasswordModal}
                          disabled={changePasswordSaving}
                          className="px-4 py-2 rounded-lg text-slate-700 dark:text-slate-200 text-sm font-semibold border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors disabled:opacity-50"
                        >
                          {t('common:cancel')}
                        </button>
                        <button
                          type="submit"
                          disabled={changePasswordSaving}
                          className="px-4 py-2 rounded-lg bg-primary text-white text-sm font-bold shadow-md hover:bg-primary-hover transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                        >
                          {changePasswordSaving ? (
                            <>
                              <span className="material-symbols-outlined text-[18px] animate-spin">progress_activity</span>
                              <span>{t('common:saving')}</span>
                            </>
                          ) : (
                            <>
                              <span className="material-symbols-outlined text-[18px]">save</span>
                              <span>{t('common:save')}</span>
                            </>
                          )}
                        </button>
                      </div>
                    </form>
                  </motion.div>
                </div>
              )}
            </AnimatePresence>
      </main>
    </div>
  );
};

export default AdminSettings;

